import { useCallback, useEffect, useState } from "react";
import {
  expandFoodAraIngestion,
  fetchFoodAraIngestionExpansionPlan,
  fetchFoodBasketCoverageBaseline,
} from "../../../../api/foodApi";
import { FOOD_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";

const FoodAraIngestionExpansionSection = () => {
  const [plan, setPlan] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [maxProducts, setMaxProducts] = useState(50);
  const [selectedFixtures, setSelectedFixtures] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [realRunConfirmed, setRealRunConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadPlan = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [planPayload, baselinePayload] = await Promise.all([
        fetchFoodAraIngestionExpansionPlan(),
        fetchFoodBasketCoverageBaseline({ sourceKey: FOOD_SOURCE_KEYS.ARA_COLOMBIA }),
      ]);
      setPlan(planPayload);
      setBaseline(baselinePayload);
      const defaults = planPayload.defaultFixtureNames?.length
        ? planPayload.defaultFixtureNames
        : (planPayload.availableFixtures || []).map((row) => row.filename);
      setSelectedFixtures(defaults);
    } catch (error) {
      setErrorMessage(error.message || "Failed to load expansion plan.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const toggleFixture = (filename) => {
    setSelectedFixtures((current) =>
      current.includes(filename)
        ? current.filter((name) => name !== filename)
        : [...current, filename]
    );
  };

  const handleExpand = async () => {
    if (!dryRun && !realRunConfirmed) {
      setErrorMessage("Confirm real run before inserting observations.");
      return;
    }
    try {
      setRunning(true);
      setErrorMessage("");
      const payload = await expandFoodAraIngestion({
        sourceKey: FOOD_SOURCE_KEYS.ARA_COLOMBIA,
        mode: "fixture",
        fixtureNames: selectedFixtures,
        maxProductsPerFixture: Number(maxProducts) || 50,
        dryRun,
      });
      setResult(payload);
      if (!dryRun) {
        await loadPlan();
      }
    } catch (error) {
      setErrorMessage(error.message || "Expansion failed.");
    } finally {
      setRunning(false);
    }
  };

  const strategy = plan?.strategy;

  return (
    <section className="panel-card food-retail-ingestion-page__normalization">
      <div className="food-retail-ingestion-page__section-header">
        <div>
          <h2>ARA Coverage Expansion</h2>
          <p className="muted">
            Targeted fixture ingestion for missing basket staples. Defaults to dry-run. Requires
            admin confirmation for real insertion.
          </p>
        </div>
        <button type="button" className="btn btn-secondary" onClick={loadPlan} disabled={loading}>
          Refresh
        </button>
      </div>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {loading && !plan ? <p className="muted">Loading expansion plan…</p> : null}

      {plan ? (
        <>
          <div className="food-retail-ingestion-page__kpi-row">
            <div>
              <span className="muted">Strategy</span>
              <strong>{strategy?.selectedStrategy}</strong>
            </div>
            <div>
              <span className="muted">Coverage</span>
              <strong>
                {baseline?.summary?.pricedItemCount ?? "—"} /{" "}
                {baseline?.summary?.basketItemCount ?? "—"} (
                {baseline?.summary?.coveragePercent ?? "—"}%)
              </strong>
            </div>
            <div>
              <span className="muted">Observations</span>
              <strong>{plan.observationCount}</strong>
            </div>
            <div>
              <span className="muted">Fixture mode</span>
              <strong>{strategy?.fixtureModeAllowed ? "Yes" : "No"}</strong>
            </div>
          </div>

          {plan.targetPlan?.recommendedImmediateTargets?.length ? (
            <p className="muted food-retail-ingestion-page__hint">
              Immediate targets: {plan.targetPlan.recommendedImmediateTargets.join(", ")}
            </p>
          ) : null}

          <div className="food-retail-ingestion-page__hint">
            <strong>Available fixtures</strong>
            <ul className="food-retail-ingestion-page__checkbox-list">
              {(plan.availableFixtures || []).map((fixture) => (
                <li key={fixture.filename}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedFixtures.includes(fixture.filename)}
                      onChange={() => toggleFixture(fixture.filename)}
                    />
                    {fixture.filename} ({fixture.status}) — {fixture.targetStaples?.join(", ")}
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className="food-retail-ingestion-page__run-controls">
            <label>
              <input
                type="checkbox"
                checked={dryRun}
                onChange={(event) => {
                  setDryRun(event.target.checked);
                  if (event.target.checked) setRealRunConfirmed(false);
                }}
              />
              Dry run (no DB insert)
            </label>
            {!dryRun ? (
              <label>
                <input
                  type="checkbox"
                  checked={realRunConfirmed}
                  onChange={(event) => setRealRunConfirmed(event.target.checked)}
                />
                I confirm this real expansion run
              </label>
            ) : null}
            <label>
              Max products per fixture
              <input
                type="number"
                min={1}
                max={100}
                value={maxProducts}
                onChange={(event) => setMaxProducts(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={running || !selectedFixtures.length}
              onClick={handleExpand}
            >
              {running ? "Running…" : dryRun ? "Run dry expansion" : "Run real expansion"}
            </button>
          </div>

          {result ? (
            <div className="food-retail-ingestion-page__hint">
              <strong>Last result</strong>
              <p>
                Parsed: {result.productsParsed} · Prepared: {result.productsPrepared} · Inserted:{" "}
                {result.observationsInserted} · Before: {result.observationCountBefore} · After:{" "}
                {result.observationCountAfter}
              </p>
              {result.likelyCoveredStaples?.length ? (
                <p>Likely covered: {result.likelyCoveredStaples.join(", ")}</p>
              ) : null}
              {result.stapleEvidence?.length ? (
                <ul>
                  {result.stapleEvidence
                    .filter((row) => row.likelyCovered)
                    .map((row) => (
                      <li key={row.canonicalProductName}>
                        {row.canonicalProductName}: {row.observedCandidateCount} candidate(s) —{" "}
                        {(row.sampleRawProductNames || []).slice(0, 2).join("; ")}
                      </li>
                    ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};

export default FoodAraIngestionExpansionSection;
